import type { ComponentProps } from 'react';

import { Alert, Text } from '@change/ds-core-ui/next';

import { Button, ButtonLink } from '@change-corgi/design-system-legacy/components/actions';

import { usePageBanner } from './hooks/usePageBanner';

type ActionButton = {
	type: 'button';
	text: string;
} & Pick<ComponentProps<typeof Button>, 'icon' | 'iconPosition' | 'mode' | 'onClick'>;

type ActionLink = {
	type: 'link';
	text: string;
} & Pick<
	ComponentProps<typeof ButtonLink>,
	'to' | 'icon' | 'iconPosition' | 'mode' | 'target' | 'forceMode' | 'onClick'
>;

type Action = ActionButton | ActionLink;
export function PageBanner() {
	const { data, actions } = usePageBanner();

	if (!data.banner) return null;

	function PrimaryActionComponent(props: Action) {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		const testIds = { 'data-testid': 'page-message-primary-action', 'data-qa': 'page-message-primary-action' };
		// eslint-disable-next-line react/destructuring-assignment
		if (props.type === 'button') {
			const { type, text, ...buttonProps } = props;
			return (
				// eslint-disable-next-line @change-corgi/require-data-qa-attribute
				<Button {...testIds} {...buttonProps} variant="secondary" size="small" sx={{ mr: 16 }}>
					{text}
				</Button>
			);
		}

		const { type, text, to, ...linkProps } = props;
		return (
			// eslint-disable-next-line @change-corgi/require-data-qa-attribute
			<ButtonLink {...testIds} {...linkProps} to={to} variant="secondary" size="small" sx={{ mr: 16 }}>
				{text}
			</ButtonLink>
		);
	}

	return (
		<Alert
			className="items-center justify-around xl:px-6 [&>div>div]:flex-row"
			type="page"
			containerClassName="justify-between w-full items-center"
			variant="success"
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
			onDismissClick={(e) => actions.onDismiss(e as unknown as React.MouseEvent<HTMLButtonElement>)}
			dismissButtonAriaLabel="Close Alert"
		>
			<div className="block max-w-full gap-3 lg:flex lg:w-[40rem] lg:items-center lg:justify-between xl:w-[50rem]">
				<Text as="p" size="large" fontWeight="bold" className="text-typography-success pb-3">
					{data.banner.message}
				</Text>
				{data.banner.primaryAction && <PrimaryActionComponent {...data.banner.primaryAction} />}
			</div>
		</Alert>
	);
}
